/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { doc, setDoc, deleteField } from "firebase/firestore";
import { db } from "../../../firebase";
import Swal from "sweetalert2";
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import { fetchStore } from "../../../redux/store/store";
import { ALGERIAN_SHIPPING_COMPANIES } from "../../../constants/algerian_shipping_companies";

export default function ActivationOrderExporting({ close }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const store = useSelector((state) => state.StoreReducer);

  const [loading, setLoading] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [data, setData] = useState({});

  useEffect(() => {
    const storeData = store || {};
    setData(storeData);

    const saved = Array.isArray(storeData.orderExportingCompanies)
      ? storeData.orderExportingCompanies
      : [];
    setSelectedCompanies(saved);
  }, [store]);

  const isEnabled =
    data?.orderExportingEnabled === true ||
    data?.orderExportingEnabled === "true";

  // all companies except "other"
  const allCompanies = useMemo(
    () => ALGERIAN_SHIPPING_COMPANIES.filter((c) => c.key !== "other" && c.supportsOrderExporting === true,),
    [],
  );

  const displayedCompanies = isEnabled
    ? allCompanies.filter((c) => selectedCompanies.includes(c.key))
    : allCompanies;

  const toggleCompany = (key) => {
    if (isEnabled) return;

    setSelectedCompanies((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleToggle = async () => {
    if (!data?.id) {
      Swal.fire({
        icon: "error",
        title: t("services.order_exporting.error_title"),
        text: t("services.order_exporting.error_store_not_found"),
        confirmButtonText: t("services.ok"),
        confirmButtonColor: "rgb(141, 93, 167)",
      });
      return;
    }

    if (!isEnabled && selectedCompanies.length === 0) {
      Swal.fire({
        icon: "warning",
        title: t("services.order_exporting.error_title"),
        text: t("services.order_exporting.select_at_least_one"),
        confirmButtonText: t("services.ok"),
        confirmButtonColor: "rgb(141, 93, 167)",
      });
      return;
    }

    setLoading(true);

    try {
      const newValue = !isEnabled;

      const payload = {
        orderExportingEnabled: newValue,
        orderExportingCompanies: newValue ? selectedCompanies : deleteField(),
      };

      await setDoc(doc(db, "store", data.id), payload, { merge: true });

      const updatedData = {
        ...data,
        orderExportingEnabled: newValue,
      };

      if (newValue) {
        updatedData.orderExportingCompanies = selectedCompanies;
      } else {
        delete updatedData.orderExportingCompanies;
      }

      setData(updatedData);
      dispatch(fetchStore(updatedData));

      await Swal.fire({
        icon: "success",
        title: newValue
          ? t("services.order_exporting.activate_success_title")
          : t("services.order_exporting.deactivate_success_title"),
        text: newValue
          ? t("services.order_exporting.activate_success_text")
          : t("services.order_exporting.deactivate_success_text"),
        confirmButtonText: t("services.ok"),
        confirmButtonColor: "rgb(141, 93, 167)",
      });

      if (close) close();
    } catch (error) {
      console.error("Error toggling Order Exporting:", error);
      Swal.fire({
        icon: "error",
        title: t("services.order_exporting.error_title"),
        text: error.message || t("services.order_exporting.error_update"),
        confirmButtonText: t("services.ok"),
        confirmButtonColor: "rgb(141, 93, 167)",
      });
    } finally {
      setLoading(false);
    }
  };

  const canActivate = !isEnabled && selectedCompanies.length > 0;
  const isButtonDisabled = loading || (!isEnabled && !canActivate);

  return (
    <div className="p-6 bg-white dark:!bg-[#181819]">
      <div className="flex items-start gap-4 mb-6">
        <div className="rounded-xl border border-[#fbf4ff] dark:!border-[#3c3a3d] p-3 w-14 h-14 flex items-center justify-center bg-purple-50 dark:!bg-[#2a2a2a] shrink-0">
          <ArrowDownTrayIcon className="w-7 h-7 text-[#9e68ba]" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-[#9e68ba] dark:!text-[#b48acb] mb-1">
            {t("services.services.order_exporting.name")}
          </h3>
          <p className="text-sm text-gray-500 dark:!text-gray-400">
            {t("services.order_exporting.activation_subtitle")}
          </p>
        </div>
      </div>

      {displayedCompanies.length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 dark:!text-gray-200 mb-3 text-sm">
            {isEnabled
              ? t("services.order_exporting.selected_companies")
              : t("services.order_exporting.select_companies")}
          </h4>

          <div className="grid grid-cols-4 gap-2.5">
            {displayedCompanies.map((company) => {
              const isSelected = selectedCompanies.includes(company.key);

              return (
                <button
                  key={company.id}
                  type="button"
                  onClick={() => toggleCompany(company.key)}
                  disabled={isEnabled}
                  className={`
                    relative flex flex-col items-center p-2.5 rounded-lg border transition-all
                    ${
                      isSelected
                        ? "border-[#9e68ba] bg-purple-50 dark:!bg-[#2a2a2a] shadow-sm"
                        : "border-gray-200 dark:!border-[#3c3a3d] bg-white dark:!bg-[#181819] hover:border-[#c9a0e0]"
                    }
                    ${isEnabled ? "cursor-default" : "cursor-pointer"}
                  `}
                >
                  {company.logoUrl ? (
                    <img
                      src={company.logoUrl}
                      alt={company.nameKey}
                      className="w-9 h-9 object-contain mb-1.5"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gray-200 dark:!bg-gray-700 flex items-center justify-center mb-1.5 text-[10px] font-bold">
                      ?
                    </div>
                  )}

                  <span className="text-[11px] font-medium text-center text-gray-700 dark:!text-gray-300 leading-tight capitalize">
                    {t(`shipping.${company.nameKey}`, {
                      defaultValue: company.nameKey,
                    })}
                  </span>

                  {isSelected && (
                    <div className="absolute top-1.5 right-1.5">
                      <CheckCircleIcon className="w-4 h-4 text-[#9e68ba]" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {!isEnabled && selectedCompanies.length === 0 && (
            <p className="text-xs text-red-500 mt-2">
              {t("services.order_exporting.select_at_least_one")}
            </p>
          )}
        </div>
      )}

      <div className="bg-purple-50 dark:!bg-[#2a2a2a] rounded-xl p-5 mb-6 border border-[#fbf4ff] dark:!border-[#3c3a3d]">
        <h4 className="font-semibold text-gray-800 dark:!text-gray-200 mb-3">
          {t("services.order_exporting.how_it_works_title")}
        </h4>
        <ul className="space-y-2 text-sm text-gray-600 dark:!text-gray-400 list-disc list-inside">
          <li>{t("services.order_exporting.how_it_works_1")}</li>
          <li>{t("services.order_exporting.how_it_works_2")}</li>
          <li>{t("services.order_exporting.how_it_works_3")}</li>
          <li>{t("services.order_exporting.how_it_works_4")}</li>
        </ul>
      </div>

      <div className="flex justify-center">
        <button
          onClick={handleToggle}
          disabled={isButtonDisabled}
          className={`
            py-2.5 px-5 rounded-lg text-sm font-medium text-white transition-colors
            flex items-center justify-center gap-2
            ${
              isEnabled
                ? "bg-red-500 hover:bg-red-600"
                : "bg-[#9b59bf] hover:bg-[#814a9f]"
            }
            ${isButtonDisabled ? "opacity-60 cursor-not-allowed" : ""}
          `}
        >
          {loading ? (
            <>
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t("services.order_exporting.updating")}
            </>
          ) : isEnabled ? (
            <>
              <XCircleIcon className="w-4 h-4" />
              {t("services.order_exporting.deactivate_btn")}
            </>
          ) : (
            <>
              <CheckCircleIcon className="w-4 h-4" />
              {t("services.order_exporting.activate_btn")}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
